import React, { useEffect, useRef, useState } from "react";
import {
  createDraft,
  isStepComplete,
  nextStep,
  prevStep,
  setRace,
  setClass,
  setAbilities,
  setBackground,
  setName,
  finalizeCharacter,
  WIZARD_STEP_ORDER,
  type CharacterDraft,
  type WizardStep,
} from "../../engine/chargen/characterDraft.js";
import type { Character } from "../../engine/types/entity.js";
import { CLASS_TABLE } from "../../engine/chargen/classData.js";
import { loadCreatorRoster, type CreatorRoster } from "../../engine/chargen/rosterLoader.js";
import { PointBuyPanel } from "./PointBuyPanel.js";
import {
  regenerateBackground,
  type RegeneratedBackground,
} from "../../engine/chargen/regeneration.js";
import {
  filterPool,
  loadPortraitPool,
  mapRaceToPool,
  pickDefaultPortrait,
  type PortraitPoolEntry,
} from "../../engine/chargen/portraitPool.js";

/**
 * Character creator wizard (C10).
 *
 * Thin React shell around the `characterDraft` state machine. Steps:
 *   race → class → abilities → background → name → review
 *
 * Hardcoded race/class/background lists for v1 — the wizard does not
 * fetch from DataRegistry, which keeps the component self-contained.
 * Authoring additions go in the SRD JSON plus this list; the overlay
 * layer runs on the final Character record elsewhere.
 */

interface Props {
  readonly onComplete: (character: Character) => void;
  readonly onCancel?: () => void;
}

const DEFAULT_RACES = [
  { id: "human", label: "Human" },
  { id: "high-elf", label: "High Elf" },
  { id: "hill-dwarf", label: "Hill Dwarf" },
  { id: "lightfoot-halfling", label: "Lightfoot Halfling" },
  { id: "half-orc", label: "Half-Orc" },
  { id: "tiefling", label: "Tiefling" },
];

const DEFAULT_BACKGROUNDS = [
  { id: "soldier", label: "Soldier" },
  { id: "sage", label: "Sage" },
  { id: "criminal", label: "Criminal" },
  { id: "acolyte", label: "Acolyte" },
  { id: "outlander", label: "Outlander" },
];

export function CharacterCreator({ onComplete, onCancel }: Props) {
  const [draft, setDraft] = useState<CharacterDraft>(createDraft());
  const [pool, setPool] = useState<readonly PortraitPoolEntry[]>([]);
  // EV-E7: adventures with a manifest `roster` swap in their own
  // class/race/background lists; everyone else keeps the classics.
  const [roster, setRoster] = useState<CreatorRoster | null>(null);
  useEffect(() => {
    void loadCreatorRoster().then(setRoster);
  }, []);
  const RACES = roster?.races ?? DEFAULT_RACES;
  const CLASSES = roster?.classes ?? CLASS_TABLE;
  const BACKGROUNDS = roster?.backgrounds ?? DEFAULT_BACKGROUNDS;
  const [selectedPortrait, setSelectedPortrait] = useState<PortraitPoolEntry | null>(null);
  const [background, setBackgroundState] = useState<RegeneratedBackground | null>(null);
  const [backgroundBusy, setBackgroundBusy] = useState(false);
  const generatedForRef = useRef<string>("");
  const defaultedForRef = useRef<string>("");

  const canAdvance = isStepComplete(draft);

  useEffect(() => {
    let cancelled = false;
    void loadPortraitPool().then((entries) => {
      if (!cancelled) setPool(entries);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // When race or class changes, re-seed the portrait selection to the
  // class-appropriate default from the pool. The player can then scroll
  // through the picker to change it.
  useEffect(() => {
    if (!draft.race || !draft.characterClass || pool.length === 0) return;
    const sig = `${draft.race}|${draft.characterClass}`;
    if (defaultedForRef.current === sig) return;
    defaultedForRef.current = sig;
    const def = pickDefaultPortrait(pool, {
      race: draft.race,
      characterClass: draft.characterClass,
    });
    setSelectedPortrait(def);
  }, [draft.race, draft.characterClass, pool]);

  const handleRegenerateBackground = async () => {
    if (!draft.race || !draft.characterClass) return;
    setBackgroundBusy(true);
    try {
      const result = await regenerateBackground({
        race: draft.race,
        characterClass: draft.characterClass,
        description: "",
      });
      setBackgroundState(result);
    } finally {
      setBackgroundBusy(false);
    }
  };

  useEffect(() => {
    if (draft.step !== "review") return;
    const sig = `${draft.race}|${draft.characterClass}|${draft.background}`;
    if (generatedForRef.current === sig) return;
    generatedForRef.current = sig;
    void handleRegenerateBackground();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft.step, draft.race, draft.characterClass, draft.background]);

  const handleFinalize = () => {
    const classData = CLASSES.find((c) => c.id === draft.characterClass);
    if (!classData) return;
    const character = finalizeCharacter({
      draft,
      instanceId: `pc-${Date.now().toString(36)}`,
      maxHp: classData.maxHp,
      armorClass: classData.ac,
      spellSlots: classData.spellSlots ?? {},
      ...(classData.castingAbility !== undefined
        ? { castingAbility: classData.castingAbility }
        : {}),
      portrait: selectedPortrait?.path ?? null,
      ...(background?.background.skillProficiencies
        ? { skillProficiencies: background.background.skillProficiencies }
        : {}),
    });
    onComplete(character);
  };

  return (
    <div style={panelStyle}>
      <StepHeader step={draft.step} />

      <div style={{ flex: 1, minHeight: 320 }}>
        {draft.step === "race" && (
          <Picker
            label="Choose a race"
            options={RACES}
            selectedId={draft.race}
            onSelect={(id) => setDraft(setRace(draft, id))}
          />
        )}

        {draft.step === "class" && (
          <Picker
            label="Choose a class"
            options={CLASSES}
            selectedId={draft.characterClass}
            onSelect={(id) => setDraft(setClass(draft, id))}
          />
        )}

        {draft.step === "abilities" && (
          <div>
            <p style={hintStyle}>Allocate your ability scores (27 points).</p>
            <PointBuyPanel
              scores={draft.abilities}
              onChange={(abilities) => setDraft(setAbilities(draft, abilities))}
            />
          </div>
        )}

        {draft.step === "background" && (
          <Picker
            label="Choose a background"
            options={BACKGROUNDS}
            selectedId={draft.background}
            onSelect={(id) => setDraft(setBackground(draft, id))}
          />
        )}

        {draft.step === "name" && (
          <div>
            <label style={{ ...hintStyle, display: "block" }} htmlFor="char-name">
              Name your character
            </label>
            <input
              id="char-name"
              type="text"
              value={draft.name}
              onChange={(e) => setDraft(setName(draft, e.target.value))}
              style={inputStyle}
              maxLength={32}
              autoFocus
            />
          </div>
        )}

        {draft.step === "review" && (
          <ReviewPanel
            draft={draft}
            pool={pool}
            selectedPortrait={selectedPortrait}
            onSelectPortrait={setSelectedPortrait}
            background={background}
            backgroundBusy={backgroundBusy}
            onRegenerateBackground={handleRegenerateBackground}
          />
        )}
      </div>

      <Footer
        draft={draft}
        canAdvance={canAdvance}
        onBack={() => setDraft(prevStep(draft))}
        onNext={() => setDraft(nextStep(draft))}
        onFinish={handleFinalize}
        onCancel={onCancel}
      />
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StepHeader({ step }: { step: WizardStep }) {
  const idx = WIZARD_STEP_ORDER.indexOf(step);
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 11 }}>
        Step {idx + 1} of {WIZARD_STEP_ORDER.length} — {step}
      </div>
      <div
        style={{
          height: 4,
          background: "rgba(255,255,255,0.1)",
          borderRadius: 2,
          marginTop: 4,
        }}
      >
        <div
          style={{
            height: "100%",
            width: `${((idx + 1) / WIZARD_STEP_ORDER.length) * 100}%`,
            background: "#6cf",
            borderRadius: 2,
          }}
        />
      </div>
    </div>
  );
}

interface PickerOption {
  readonly id: string;
  readonly label: string;
}

function Picker({
  label,
  options,
  selectedId,
  onSelect,
}: {
  label: string;
  options: readonly PickerOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  return (
    <div>
      <p style={hintStyle}>{label}</p>
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {options.map((opt) => (
          <button
            key={opt.id}
            type="button"
            onClick={() => onSelect(opt.id)}
            style={{
              ...optionButtonStyle,
              background: selectedId === opt.id ? "#2a6" : "#333",
              borderColor: selectedId === opt.id ? "#6cf" : "rgba(255,255,255,0.2)",
            }}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function ReviewPanel({
  draft,
  pool,
  selectedPortrait,
  onSelectPortrait,
  background,
  backgroundBusy,
  onRegenerateBackground,
}: {
  draft: CharacterDraft;
  pool: readonly PortraitPoolEntry[];
  selectedPortrait: PortraitPoolEntry | null;
  onSelectPortrait: (entry: PortraitPoolEntry) => void;
  background: RegeneratedBackground | null;
  backgroundBusy: boolean;
  onRegenerateBackground: () => void;
}) {
  // Don't hard-filter — players can pick any portrait. Sort so the
  // race+class match shows up first, then same race, then the rest. The
  // default pick lands on a class-appropriate portrait of the chosen
  // race but every one of the 100 pool entries is reachable via cycle.
  const poolRace = draft.race ? mapRaceToPool(draft.race) : null;
  const cls = draft.characterClass?.trim().toLowerCase() ?? null;
  const available = [...pool].sort((a, b) => {
    const raceA = poolRace && a.race === poolRace ? 0 : 1;
    const raceB = poolRace && b.race === poolRace ? 0 : 1;
    if (raceA !== raceB) return raceA - raceB;
    const clsA = cls && a.classAffinities.some((c) => c.toLowerCase() === cls) ? 0 : 1;
    const clsB = cls && b.classAffinities.some((c) => c.toLowerCase() === cls) ? 0 : 1;
    return clsA - clsB;
  });
  const currentIdx = selectedPortrait
    ? available.findIndex((e) => e.id === selectedPortrait.id)
    : -1;

  const step = (delta: number) => {
    if (available.length === 0) return;
    const base = currentIdx < 0 ? 0 : currentIdx;
    const next = (base + delta + available.length) % available.length;
    const entry = available[next];
    if (entry) onSelectPortrait(entry);
  };

  return (
    <div style={{ color: "white", fontFamily: "monospace", fontSize: 13 }}>
      <p style={hintStyle}>Ready to begin the descent?</p>
      <div style={{ display: "flex", gap: 12, marginBottom: 12 }}>
        <div
          data-testid="portrait-slot"
          style={{
            width: 96,
            height: 96,
            background: "#c9c1b0",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 4,
            overflow: "hidden",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {selectedPortrait ? (
            <img
              src={selectedPortrait.path}
              alt="character portrait"
              style={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
          ) : (
            <span style={{ fontSize: 10, color: "#888", textAlign: "center", padding: 4 }}>
              no portraits for this race
            </span>
          )}
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ display: "flex", gap: 4 }}>
            <button
              type="button"
              onClick={() => step(-1)}
              disabled={available.length === 0}
              style={{ ...footerBtn, fontSize: 12, flex: 1 }}
              aria-label="previous portrait"
            >
              ‹
            </button>
            <button
              type="button"
              onClick={() => step(1)}
              disabled={available.length === 0}
              style={{ ...footerBtn, fontSize: 12, flex: 1 }}
              aria-label="next portrait"
            >
              ›
            </button>
          </div>
          {selectedPortrait && (
            <span style={{ fontSize: 10, color: "#888" }}>
              {selectedPortrait.race} {selectedPortrait.gender} · {currentIdx + 1}/
              {available.length}
            </span>
          )}
        </div>
      </div>
      <dl style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 12px", margin: 0 }}>
        <dt style={dtStyle}>Name</dt>
        <dd style={ddStyle}>{draft.name}</dd>
        <dt style={dtStyle}>Race</dt>
        <dd style={ddStyle}>{draft.race}</dd>
        <dt style={dtStyle}>Class</dt>
        <dd style={ddStyle}>{draft.characterClass}</dd>
        <dt style={dtStyle}>Background</dt>
        <dd style={ddStyle}>{draft.background}</dd>
        <dt style={dtStyle}>Abilities</dt>
        <dd style={ddStyle}>
          STR {draft.abilities.STR} · DEX {draft.abilities.DEX} · CON {draft.abilities.CON} · INT{" "}
          {draft.abilities.INT} · WIS {draft.abilities.WIS} · CHA {draft.abilities.CHA}
        </dd>
      </dl>
      <div style={{ marginTop: 12 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 4,
          }}
        >
          <span style={{ color: "rgba(255,255,255,0.6)", fontSize: 11 }}>
            Backstory {background && `(${background.source})`}
          </span>
          <button
            type="button"
            onClick={onRegenerateBackground}
            disabled={backgroundBusy}
            style={{ ...footerBtn, fontSize: 11, padding: "4px 8px", opacity: backgroundBusy ? 0.5 : 1 }}
          >
            {backgroundBusy ? "Generating…" : "Regenerate backstory"}
          </button>
        </div>
        <div
          data-testid="backstory-slot"
          style={{
            background: "#222",
            border: "1px solid rgba(255,255,255,0.2)",
            borderRadius: 4,
            padding: 8,
            fontSize: 11,
            lineHeight: 1.5,
            color: "#ddd",
            minHeight: 60,
          }}
        >
          {backgroundBusy
            ? "…"
            : background
              ? background.background.backstory
              : ""}
        </div>
      </div>
    </div>
  );
}

function Footer({
  draft,
  canAdvance,
  onBack,
  onNext,
  onFinish,
  onCancel,
}: {
  draft: CharacterDraft;
  canAdvance: boolean;
  onBack: () => void;
  onNext: () => void;
  onFinish: () => void;
  onCancel?: (() => void) | undefined;
}) {
  const idx = WIZARD_STEP_ORDER.indexOf(draft.step);
  const isFirst = idx === 0;
  const isLast = draft.step === "review";
  return (
    <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
      {onCancel && (
        <button type="button" onClick={onCancel} style={{ ...footerBtn, background: "#442" }}>
          Cancel
        </button>
      )}
      <div style={{ flex: 1 }} />
      <button type="button" onClick={onBack} disabled={isFirst} style={footerBtn}>
        Back
      </button>
      {isLast ? (
        <button type="button" onClick={onFinish} style={{ ...footerBtn, background: "#2a6" }}>
          Begin
        </button>
      ) : (
        <button
          type="button"
          onClick={onNext}
          disabled={!canAdvance}
          style={{
            ...footerBtn,
            background: canAdvance ? "#2a6" : "#333",
            opacity: canAdvance ? 1 : 0.5,
          }}
        >
          Next
        </button>
      )}
    </div>
  );
}

// ── Styles ───────────────────────────────────────────────────────────────────

const panelStyle: React.CSSProperties = {
  background: "#1a1a1a",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 8,
  padding: 24,
  color: "white",
  fontFamily: "monospace",
  width: 420,
  display: "flex",
  flexDirection: "column",
  pointerEvents: "auto",
};

const hintStyle: React.CSSProperties = {
  color: "rgba(255,255,255,0.7)",
  fontSize: 13,
  margin: "0 0 12px 0",
};

const optionButtonStyle: React.CSSProperties = {
  padding: "8px 12px",
  borderRadius: 4,
  color: "white",
  fontFamily: "monospace",
  fontSize: 13,
  cursor: "pointer",
  textAlign: "left",
  border: "1px solid",
};

const inputStyle: React.CSSProperties = {
  width: "100%",
  padding: "8px 12px",
  background: "#222",
  color: "white",
  border: "1px solid rgba(255,255,255,0.3)",
  borderRadius: 4,
  fontFamily: "monospace",
  fontSize: 14,
  boxSizing: "border-box",
};

const footerBtn: React.CSSProperties = {
  padding: "8px 16px",
  background: "#333",
  color: "white",
  border: "1px solid rgba(255,255,255,0.2)",
  borderRadius: 4,
  fontFamily: "monospace",
  fontSize: 13,
  cursor: "pointer",
};

const dtStyle: React.CSSProperties = { color: "rgba(255,255,255,0.6)" };
const ddStyle: React.CSSProperties = { margin: 0, color: "white" };
